/**
 * useAuth Hook
 *
 * Handles authentication state for both admin and customer users.
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';

import { getAuthToken, getCustomerToken } from '../services/api/client';

/**
 * Hook for admin authentication state
 *
 * @returns {Object} Admin auth state and methods
 */
export function useAdminAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      try {
        // Decode JWT to get user info (basic decode, not verification)
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Check if token is expired
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          logout();
        } else {
          setIsAuthenticated(true);
          setUser({
            email: payload.email || payload.sub,
            name: payload.name,
            picture: payload.picture,
          });
        }
      } catch (err) {
        // Invalid token
        logout();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((token, userData = null) => {
    localStorage.setItem('googleAuthToken', token);
    setIsAuthenticated(true);

    if (userData) {
      setUser(userData);
    } else {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          email: payload.email || payload.sub,
          name: payload.name,
          picture: payload.picture,
        });
      } catch {
        setUser(null);
      }
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('googleAuthToken');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  return {
    isAuthenticated,
    loading,
    user,
    login,
    logout,
    token: getAuthToken(),
  };
}

/**
 * Hook for customer authentication state
 *
 * @returns {Object} Customer auth state and methods
 */
export function useCustomerAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    const token = getCustomerToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Check if token is expired
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          logout();
        } else {
          setIsAuthenticated(true);
          setCustomer({
            id: payload.sub,
            email: payload.email,
            name: payload.name,
          });
        }
      } catch {
        logout();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((token, customerData = null) => {
    localStorage.setItem('customerToken', token);
    setIsAuthenticated(true);

    if (customerData) {
      setCustomer(customerData);
    } else {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCustomer({
          id: payload.sub,
          email: payload.email,
          name: payload.name,
        });
      } catch {
        setCustomer(null);
      }
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('customerToken');
    setIsAuthenticated(false);
    setCustomer(null);
  }, []);

  return {
    isAuthenticated,
    loading,
    customer,
    login,
    logout,
    token: getCustomerToken(),
  };
}

// Auth Context for providing auth state throughout the app
const AdminAuthContext = createContext(null);
const CustomerAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const auth = useAdminAuth();
  return (
    <AdminAuthContext.Provider value={auth}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function CustomerAuthProvider({ children }) {
  const auth = useCustomerAuth();
  return (
    <CustomerAuthContext.Provider value={auth}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useAdminAuthContext() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuthContext must be used within AdminAuthProvider');
  }
  return context;
}

export function useCustomerAuthContext() {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuthContext must be used within CustomerAuthProvider');
  }
  return context;
}

export default {
  useAdminAuth,
  useCustomerAuth,
  AdminAuthProvider,
  CustomerAuthProvider,
  useAdminAuthContext,
  useCustomerAuthContext,
};
